import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs, doc, getDoc, setDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useNavigate } from 'react-router-dom';
import { db, auth } from '../firebase/config';
import { Siren, Search, Send, Inbox, Store, Plus, Trash2, X, MapPin, CheckCircle2 } from 'lucide-react';

/**
 * 긴급외주요청 (/emergency-outsource, 기공소 전용)
 * - 장비 고장·인력 부족 등 급할 때, 다른 기공소가 등록한 이용단가를 보고 긴급 외주 요청
 * - 수락 시 거래처 자동 연결 (수수료는 2026-08-17 전면 폐지 — 부과 없음)
 * - 탭: 업체 찾기 / 보낸 요청 / 받은 요청 / 내 서비스 등록
 */
const SERVICE_TYPES = ['디자인(CAD)', '밀링', '세라믹 빌드업', '덴처', '임시치아', '3D프린팅', '주조', '기타'];
const UNITS = ['건', '치아(본)', '시간', '악'];

const STATUS_LABEL = {
    requested: { text: '수락 대기', color: '#D97706', bg: '#FFFBEB' },
    accepted: { text: '수락됨', color: '#2563EB', bg: '#EFF6FF' },
    rejected: { text: '거절됨', color: '#DC2626', bg: '#FEF2F2' },
    in_progress: { text: '작업중', color: '#7C3AED', bg: '#F5F3FF' },
    delivered: { text: '납품완료', color: '#059669', bg: '#ECFDF5' },
    completed: { text: '완료', color: '#6B7280', bg: '#F3F4F6' },
    cancelled: { text: '취소됨', color: '#9CA3AF', bg: '#F3F4F6' },
};

const fmt = (n) => (n || 0).toLocaleString('ko-KR');
const inputStyle = { width: '100%', padding: '9px 12px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' };
const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 5, color: '#374151' };
const btnStyle = (bg) => ({ display: 'inline-flex', alignItems: 'center', gap: 6, background: bg, color: '#fff', border: 'none', padding: '9px 14px', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer' });

function EmergencyOutsource({ user }) {
    const navigate = useNavigate();
    const uid = auth.currentUser?.uid;
    const myId = user?.companyId || uid;

    const [tab, setTab] = useState('find');
    const [providers, setProviders] = useState([]);
    const [sent, setSent] = useState([]);
    const [received, setReceived] = useState([]);
    const [loading, setLoading] = useState(true);
    const [working, setWorking] = useState(false);

    // 긴급요청 모달
    const [target, setTarget] = useState(null);          // 선택한 수주 기공소
    const [reqForm, setReqForm] = useState({ serviceIdx: 0, quantity: 1, dueDate: '', notes: '' });

    // 내 서비스 등록
    const [myService, setMyService] = useState({ active: true, description: '', services: [] });
    const [savingService, setSavingService] = useState(false);

    const businessType = user?.businessType || user?.userType;
    const isLab = businessType === 'lab' || businessType === 'outsource';

    const fetchAll = useCallback(async () => {
        if (!uid) return;
        setLoading(true);
        try {
            // 수주 등록 업체 (본인 제외, active만)
            const provSnap = await getDocs(query(collection(db, 'emergencyServices'), where('active', '==', true)));
            setProviders(provSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(p => p.id !== uid && p.id !== myId && (p.services || []).length > 0));

            // 보낸/받은 요청
            const ids = [...new Set([uid, myId].filter(Boolean))];
            const sentList = [], recvList = [];
            for (const id of ids) {
                const s = await getDocs(query(collection(db, 'emergencyRequests'), where('fromLabId', '==', id)));
                s.forEach(d => sentList.push({ id: d.id, ...d.data() }));
                const r = await getDocs(query(collection(db, 'emergencyRequests'), where('toLabId', '==', id)));
                r.forEach(d => recvList.push({ id: d.id, ...d.data() }));
            }
            const byTime = (a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0);
            setSent(sentList.sort(byTime));
            setReceived(recvList.sort(byTime));

            // 내 서비스
            const mySnap = await getDoc(doc(db, 'emergencyServices', myId || uid));
            if (mySnap.exists()) setMyService(prev => ({ ...prev, ...mySnap.data() }));
        } catch (e) {
            console.error('긴급외주 데이터 조회 실패:', e);
        } finally {
            setLoading(false);
        }
    }, [uid, myId]);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    if (!isLab) {
        return <div style={{ maxWidth: 640, margin: '60px auto', textAlign: 'center', color: '#6B7280' }}>긴급외주요청은 기공소 회원 전용입니다.</div>;
    }

    const callFn = (name) => httpsCallable(getFunctions(undefined, 'asia-northeast3'), name);

    // ── 긴급요청 보내기 ──
    const submitRequest = async () => {
        const svc = (target.services || [])[reqForm.serviceIdx];
        if (!svc) { alert('서비스를 선택해주세요.'); return; }
        const quantity = Number(reqForm.quantity) || 1;
        const amount = (svc.unitPrice || 0) * quantity;
        if (!window.confirm(`${target.labName}에 긴급요청을 보낼까요?\n\n${svc.name} × ${quantity}${svc.unit || ''} = 예상 ${fmt(amount)}원`)) return;

        setWorking(true);
        try {
            const me = user || {};
            await addDoc(collection(db, 'emergencyRequests'), {
                fromLabId: uid,
                fromLabName: me.businessName || me.companyName || me.name || '',
                fromLabPhone: me.phone || '',
                toLabId: target.id,
                toLabName: target.labName || '',
                serviceType: svc.type || '',
                serviceName: svc.name || '',
                unitPrice: svc.unitPrice || 0,
                unit: svc.unit || '건',
                quantity,
                amount,
                dueDate: reqForm.dueDate || '',
                notes: reqForm.notes || '',
                status: 'requested',
                createdAt: serverTimestamp(),
            });
            alert('긴급요청을 보냈습니다. 상대 기공소가 수락하면 알려드립니다.');
            setTarget(null);
            setReqForm({ serviceIdx: 0, quantity: 1, dueDate: '', notes: '' });
            setTab('sent');
            await fetchAll();
        } catch (e) {
            alert('요청 실패: ' + e.message);
        } finally {
            setWorking(false);
        }
    };

    // ── 수락/상태 전이 ──
    const handleAccept = async (req) => {
        if (!window.confirm(`${req.fromLabName}의 긴급요청을 수락하시겠습니까?\n\n수락 시 거래처로 자동 연결됩니다. 중개 수수료는 없습니다(2026-08-17 폐지).`)) return;
        setWorking(true);
        try {
            await callFn('acceptEmergencyRequest')({ requestId: req.id });
            alert('수락했습니다. 거래처로 연결되었습니다.');
            await fetchAll();
        } catch (e) {
            const msg = e.message || '';
            if (msg.includes('NEED_BILLING_KEY')) {
                if (window.confirm('결제 카드 등록이 필요합니다.\n카드 등록 화면으로 이동할까요?')) navigate('/subscription');
            } else {
                alert(msg || '수락에 실패했습니다.');
            }
        } finally {
            setWorking(false);
        }
    };

    const handleStatus = async (req, status, confirmMsg) => {
        if (!window.confirm(confirmMsg)) return;
        setWorking(true);
        try {
            await callFn('updateEmergencyRequestStatus')({ requestId: req.id, status });
            await fetchAll();
        } catch (e) {
            alert(e.message || '상태 변경에 실패했습니다.');
        } finally {
            setWorking(false);
        }
    };

    // ── 내 서비스 저장 ──
    const saveMyService = async () => {
        const services = (myService.services || []).filter(s => s.name && s.unitPrice > 0);
        if (myService.active && services.length === 0) {
            alert('수주를 받으려면 서비스와 단가를 1개 이상 등록해주세요.'); return;
        }
        setSavingService(true);
        try {
            const me = user || {};
            await setDoc(doc(db, 'emergencyServices', myId || uid), {
                labId: myId || uid,
                labName: me.businessName || me.companyName || me.name || '',
                region: me.address ? me.address.split(' ').slice(0, 2).join(' ') : '',
                active: !!myService.active,
                description: myService.description || '',
                services,
                updatedAt: serverTimestamp(),
            });
            alert(myService.active ? '수주 등록이 완료되었습니다. 다른 기공소들에게 노출됩니다.' : '수주 접수가 비활성화되었습니다.');
            await fetchAll();
        } catch (e) {
            alert('저장 실패: ' + e.message);
        } finally {
            setSavingService(false);
        }
    };

    const setSvcField = (idx, key, value) => {
        setMyService(prev => {
            const services = [...(prev.services || [])];
            services[idx] = { ...services[idx], [key]: value };
            return { ...prev, services };
        });
    };

    const StatusBadge = ({ status }) => {
        const s = STATUS_LABEL[status] || STATUS_LABEL.requested;
        return <span style={{ fontSize: 12, fontWeight: 700, color: s.color, background: s.bg, padding: '3px 10px', borderRadius: 10 }}>{s.text}</span>;
    };

    const RequestCard = ({ req, mine }) => (
        <div style={{ border: '1px solid #E5E7EB', borderRadius: 12, padding: 16, background: '#fff' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <div style={{ fontWeight: 700, fontSize: 15 }}>
                    {mine ? `→ ${req.toLabName}` : `← ${req.fromLabName}`}
                </div>
                <StatusBadge status={req.status} />
            </div>
            <div style={{ fontSize: 14, color: '#374151', marginTop: 8, lineHeight: 1.8 }}>
                <b>{req.serviceName}</b> × {req.quantity}{req.unit} = <b>{fmt(req.amount)}원</b> (단가 {fmt(req.unitPrice)}원)
                {req.dueDate && <div>희망납기: {req.dueDate}</div>}
                {req.notes && <div style={{ color: '#6B7280' }}>요청사항: {req.notes}</div>}
                {req.status !== 'requested' && req.fromLabPhone && !mine && <div>연락처: {req.fromLabPhone}</div>}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                {!mine && req.status === 'requested' && (
                    <>
                        <button onClick={() => handleAccept(req)} disabled={working} style={btnStyle('#059669')}><CheckCircle2 size={14} /> 수락</button>
                        <button onClick={() => handleStatus(req, 'rejected', '이 요청을 거절하시겠습니까?')} disabled={working} style={btnStyle('#DC2626')}>거절</button>
                    </>
                )}
                {!mine && req.status === 'accepted' && (
                    <button onClick={() => handleStatus(req, 'in_progress', '작업을 시작하시겠습니까?')} disabled={working} style={btnStyle('#7C3AED')}>작업 시작</button>
                )}
                {!mine && ['accepted', 'in_progress'].includes(req.status) && (
                    <button onClick={() => handleStatus(req, 'delivered', '납품 완료로 변경하시겠습니까?')} disabled={working} style={btnStyle('#059669')}>납품 완료</button>
                )}
                {mine && req.status === 'delivered' && (
                    <button onClick={() => handleStatus(req, 'completed', '수령을 확인하고 완료 처리하시겠습니까?\n이후 거래명세서·세금계산서로 정산해주세요.')} disabled={working} style={btnStyle('#2563EB')}>완료 확정</button>
                )}
                {mine && req.status === 'requested' && (
                    <button onClick={() => handleStatus(req, 'cancelled', '요청을 취소하시겠습니까?')} disabled={working} style={btnStyle('#9CA3AF')}>요청 취소</button>
                )}
            </div>
        </div>
    );

    const tabBtn = (key, label, Icon, count) => (
        <button onClick={() => setTab(key)}
            style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 20, fontSize: 14, fontWeight: 600, cursor: 'pointer',
                border: tab === key ? '1px solid #DC2626' : '1px solid #E5E7EB',
                background: tab === key ? '#FEF2F2' : '#fff',
                color: tab === key ? '#DC2626' : '#6B7280',
            }}>
            <Icon size={15} /> {label}{count > 0 && <span style={{ background: '#DC2626', color: '#fff', fontSize: 11, fontWeight: 700, borderRadius: 10, padding: '1px 7px' }}>{count}</span>}
        </button>
    );

    const pendingReceived = received.filter(r => r.status === 'requested').length;

    return (
        <div style={{ maxWidth: 860, margin: '0 auto', padding: '24px 16px' }}>
            <h1 style={{ fontSize: 24, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, marginTop: 0 }}>
                <Siren size={26} color="#DC2626" /> 긴급외주요청
            </h1>
            <p style={{ color: '#6B7280', fontSize: 14, marginBottom: 20, lineHeight: 1.7 }}>
                장비 고장, 디자인 인력 부족 등 급할 때 — 다른 기공소가 등록한 이용단가를 보고 바로 긴급 외주를 요청하세요.
                수락 시 거래처로 자동 연결되며, 정산은 거래명세서·세금계산서로 진행됩니다. 중개 수수료는 없습니다(2026-08-17 폐지).
            </p>

            <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
                {tabBtn('find', '업체 찾기', Search, 0)}
                {tabBtn('sent', '보낸 요청', Send, 0)}
                {tabBtn('received', '받은 요청', Inbox, pendingReceived)}
                {tabBtn('my', '내 수주 등록', Store, 0)}
            </div>

            {loading ? (
                <div style={{ textAlign: 'center', padding: 60, color: '#9CA3AF' }}>불러오는 중...</div>
            ) : tab === 'find' ? (
                providers.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 60, color: '#9CA3AF', background: '#F9FAFB', borderRadius: 12 }}>
                        아직 수주를 등록한 기공소가 없습니다.<br />
                        <span style={{ fontSize: 13 }}>"내 수주 등록" 탭에서 우리 기공소를 먼저 등록해보세요.</span>
                    </div>
                ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 }}>
                        {providers.map(p => (
                            <div key={p.id} style={{ border: '1px solid #E5E7EB', borderRadius: 12, padding: 16, background: '#fff', display: 'flex', flexDirection: 'column' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ fontWeight: 700, fontSize: 16 }}>{p.labName}</span>
                                    {p.region && <span style={{ fontSize: 12, color: '#6B7280', display: 'flex', alignItems: 'center', gap: 3 }}><MapPin size={12} /> {p.region}</span>}
                                </div>
                                {p.description && <div style={{ fontSize: 13, color: '#6B7280', marginTop: 6, lineHeight: 1.6 }}>{p.description}</div>}
                                <div style={{ marginTop: 10, flex: 1 }}>
                                    {(p.services || []).map((s, i) => (
                                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13.5, padding: '6px 0', borderBottom: '1px solid #F3F4F6' }}>
                                            <span>{s.name} <span style={{ color: '#9CA3AF', fontSize: 12 }}>({s.type})</span></span>
                                            <span style={{ fontWeight: 700 }}>{fmt(s.unitPrice)}원/{s.unit}</span>
                                        </div>
                                    ))}
                                </div>
                                <button onClick={() => { setTarget(p); setReqForm({ serviceIdx: 0, quantity: 1, dueDate: '', notes: '' }); }}
                                    style={{ ...btnStyle('#DC2626'), justifyContent: 'center', marginTop: 12 }}>
                                    <Siren size={14} /> 긴급요청 보내기
                                </button>
                            </div>
                        ))}
                    </div>
                )
            ) : tab === 'sent' ? (
                sent.length === 0 ? <div style={{ textAlign: 'center', padding: 60, color: '#9CA3AF', background: '#F9FAFB', borderRadius: 12 }}>보낸 요청이 없습니다.</div>
                    : <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>{sent.map(r => <RequestCard key={r.id} req={r} mine />)}</div>
            ) : tab === 'received' ? (
                received.length === 0 ? <div style={{ textAlign: 'center', padding: 60, color: '#9CA3AF', background: '#F9FAFB', borderRadius: 12 }}>받은 요청이 없습니다.<br /><span style={{ fontSize: 13 }}>"내 수주 등록"에서 서비스를 등록하면 요청을 받을 수 있습니다.</span></div>
                    : <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>{received.map(r => <RequestCard key={r.id} req={r} mine={false} />)}</div>
            ) : (
                /* ── 내 수주 등록 ── */
                <div style={{ maxWidth: 640 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
                            <input type="checkbox" checked={!!myService.active} onChange={e => setMyService(prev => ({ ...prev, active: e.target.checked }))} style={{ width: 18, height: 18, accentColor: '#DC2626' }} />
                            우리 기공소 긴급외주 수주 받기
                        </label>
                    </div>
                    <div style={{ marginBottom: 14 }}>
                        <label style={labelStyle}>소개 (선택)</label>
                        <textarea value={myService.description || ''} onChange={e => setMyService(prev => ({ ...prev, description: e.target.value }))} rows={2}
                            placeholder="예: 3축 밀링기 2대 보유, 당일 디자인 가능" style={{ ...inputStyle, resize: 'vertical' }} />
                    </div>
                    <label style={labelStyle}>서비스 · 이용단가</label>
                    {(myService.services || []).map((s, i) => (
                        <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                            <select value={s.type || SERVICE_TYPES[0]} onChange={e => setSvcField(i, 'type', e.target.value)} style={{ ...inputStyle, width: 130 }}>
                                {SERVICE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                            <input value={s.name || ''} onChange={e => setSvcField(i, 'name', e.target.value)} placeholder="서비스명 (예: 크라운 디자인)" style={{ ...inputStyle, flex: 1, minWidth: 140 }} />
                            <input type="number" value={s.unitPrice || ''} onChange={e => setSvcField(i, 'unitPrice', Number(e.target.value))} placeholder="단가(원)" style={{ ...inputStyle, width: 110 }} />
                            <select value={s.unit || '건'} onChange={e => setSvcField(i, 'unit', e.target.value)} style={{ ...inputStyle, width: 90 }}>
                                {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                            </select>
                            <button type="button" onClick={() => setMyService(prev => ({ ...prev, services: prev.services.filter((_, j) => j !== i) }))}
                                style={{ background: 'none', border: 'none', color: '#DC2626', cursor: 'pointer', padding: 4 }}><Trash2 size={16} /></button>
                        </div>
                    ))}
                    <button type="button" onClick={() => setMyService(prev => ({ ...prev, services: [...(prev.services || []), { type: SERVICE_TYPES[0], name: '', unitPrice: 0, unit: '건' }] }))}
                        style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#F9FAFB', border: '1px dashed #D1D5DB', padding: '10px 14px', borderRadius: 8, fontSize: 14, fontWeight: 600, color: '#374151', cursor: 'pointer', marginBottom: 16 }}>
                        <Plus size={15} /> 서비스 추가
                    </button>
                    <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#92400E', lineHeight: 1.7, marginBottom: 16 }}>
                        요청을 수락하면 상대 기공소와 거래처로 연결됩니다. <b>중개 수수료는 없습니다</b> (2026-08-17 전면 폐지).
                    </div>
                    <button onClick={saveMyService} disabled={savingService} style={{ ...btnStyle('#DC2626'), width: '100%', justifyContent: 'center', padding: '13px' }}>
                        {savingService ? '저장 중...' : '수주 등록 저장'}
                    </button>
                </div>
            )}

            {/* 긴급요청 모달 */}
            {target && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                    <div style={{ background: '#fff', borderRadius: 16, padding: 24, maxWidth: 420, width: '92%', maxHeight: '88vh', overflowY: 'auto' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>🚨 {target.labName}에 긴급요청</h3>
                            <button onClick={() => setTarget(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280' }}><X size={20} /></button>
                        </div>
                        <p style={{ fontSize: 13, color: '#6B7280', margin: '0 0 16px' }}>수락되면 서로 연락처가 공개되고 거래처로 연결됩니다.</p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            <div>
                                <label style={labelStyle}>서비스 선택</label>
                                <select value={reqForm.serviceIdx} onChange={e => setReqForm(prev => ({ ...prev, serviceIdx: Number(e.target.value) }))} style={inputStyle}>
                                    {(target.services || []).map((s, i) => (
                                        <option key={i} value={i}>{s.name} — {fmt(s.unitPrice)}원/{s.unit}</option>
                                    ))}
                                </select>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                                <div>
                                    <label style={labelStyle}>수량</label>
                                    <input type="number" min={1} value={reqForm.quantity} onChange={e => setReqForm(prev => ({ ...prev, quantity: e.target.value }))} style={inputStyle} />
                                </div>
                                <div>
                                    <label style={labelStyle}>희망 납기</label>
                                    <input type="date" value={reqForm.dueDate} min={new Date().toISOString().slice(0, 10)} onChange={e => setReqForm(prev => ({ ...prev, dueDate: e.target.value }))} style={inputStyle} />
                                </div>
                            </div>
                            <div>
                                <label style={labelStyle}>요청 사항</label>
                                <textarea value={reqForm.notes} onChange={e => setReqForm(prev => ({ ...prev, notes: e.target.value }))} rows={3} placeholder="예: 밀링기 고장으로 지르코니아 크라운 5본 급히 부탁드립니다" style={{ ...inputStyle, resize: 'vertical' }} />
                            </div>
                            <div style={{ background: '#F9FAFB', borderRadius: 8, padding: '10px 14px', fontSize: 14 }}>
                                예상 금액: <b>{fmt(((target.services || [])[reqForm.serviceIdx]?.unitPrice || 0) * (Number(reqForm.quantity) || 1))}원</b>
                                <span style={{ fontSize: 12, color: '#9CA3AF' }}> (정산은 거래명세서로 진행)</span>
                            </div>
                            <button onClick={submitRequest} disabled={working} style={{ ...btnStyle('#DC2626'), justifyContent: 'center', padding: '13px' }}>
                                {working ? '전송 중...' : '긴급요청 보내기'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default EmergencyOutsource;
